/**
 * ============================================================================
 * TRANSIENT: Appointment Config Page (appointment-config-page)
 * Type: poc
 * Amplitude key: none
 * End date: 2026-12-31
 * PR: #TODO_AFTER_PR_CREATION_appointment-config-page
 * ============================================================================
 */

import { Box, Stack, Typography, paletteColors } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import { getActiveDaysSummary } from '../../../resources/appointment/operations';
import type {
    ActiveDaysSummary,
    ContactType,
    DayKey,
} from '../../../resources/appointment/operations';

type Section = 'slots' | 'contact' | 'params';

const SECTIONS = [
    { id: 'slots', labelId: 'appointment.sidebar.slots' },
    { id: 'contact', labelId: 'appointment.sidebar.contact' },
    { id: 'params', labelId: 'appointment.sidebar.params' },
] as const satisfies { id: Section; labelId: string }[];

const CONTACT_SUMMARY_KEY = {
    audio_request_manager: 'appointment.sidebar.contact_summary.audio',
    visio_request_manager: 'appointment.sidebar.contact_summary.visio',
    other: 'appointment.sidebar.contact_summary.other',
} as const satisfies Record<ContactType, string>;

type Props = {
    activeSection: Section;
    activeDays: Partial<Record<DayKey, boolean>>;
    defaultCallType: ContactType;
    slotDurationInMinutes: number;
    travelerReminder: boolean;
    onSectionClick: (section: Section) => void;
};

export const AppointmentSidebarPartial = ({
    activeSection,
    activeDays,
    defaultCallType,
    slotDurationInMinutes,
    travelerReminder,
    onSectionClick,
}: Props): JSX.Element => {
    const { formatMessage } = useIntl();

    const renderActiveDays = (summary: ActiveDaysSummary): string => {
        const short = (day: DayKey) => formatMessage({ id: `appointment.day.short.${day}` });
        if (summary.kind === 'none') return '—';
        if (summary.kind === 'range') return `${short(summary.from)} → ${short(summary.to)}`;
        return summary.days.map(short).join(', ');
    };

    return (
        <Box
            sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                position: 'sticky',
                top: 80,
                alignSelf: 'start',
            }}
        >
            <Stack spacing={0.5}>
                {SECTIONS.map(({ id, labelId }) => {
                    const isActive = activeSection === id;
                    return (
                        <Box
                            key={id}
                            component="button"
                            data-spec-id={
                                id === 'contact' ? 'sidebar-contact-row' : `sidebar-anchor-${id}`
                            }
                            onClick={() => onSectionClick(id)}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 0.25,
                                p: 2,
                                borderRadius: 1,
                                border: 'none',
                                cursor: 'pointer',
                                textAlign: 'left',
                                bgcolor: isActive ? paletteColors.green[100] : 'transparent',
                                transition: 'background-color 0.15s',
                                '&:hover': {
                                    bgcolor: isActive
                                        ? paletteColors.green[100]
                                        : paletteColors.neutral[100],
                                },
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 700,
                                    color: isActive ? paletteColors.green[800] : 'text.secondary',
                                    lineHeight: 1.4,
                                }}
                            >
                                {formatMessage({ id: labelId })}
                            </Typography>

                            {id === 'slots' && (
                                <Typography
                                    data-spec-id="sidebar-jours-chip"
                                    sx={{
                                        fontSize: 12,
                                        color: paletteColors.neutral[500],
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {renderActiveDays(getActiveDaysSummary(activeDays))}
                                </Typography>
                            )}

                            {id === 'contact' && (
                                <Typography
                                    data-spec-id="sidebar-contact-chip"
                                    sx={{
                                        fontSize: 12,
                                        color: paletteColors.neutral[500],
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {formatMessage({ id: CONTACT_SUMMARY_KEY[defaultCallType] })}
                                </Typography>
                            )}

                            {id === 'params' && (
                                <>
                                    <Typography
                                        data-spec-id="sidebar-duree-chip"
                                        sx={{
                                            fontSize: 12,
                                            color: paletteColors.neutral[500],
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {slotDurationInMinutes} min
                                    </Typography>
                                    <Typography
                                        data-spec-id="sidebar-rappel-chip"
                                        sx={{
                                            fontSize: 12,
                                            color: paletteColors.neutral[500],
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {formatMessage({
                                            id: travelerReminder
                                                ? 'appointment.sidebar.reminder_enabled'
                                                : 'appointment.sidebar.reminder_disabled',
                                        })}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
};
